import React from "react";
import { Shield, Copy, RotateCcw, ArrowLeft } from "lucide-react";

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorCode: string;
}

function generateErrorCode(error: Error): string {
  const timestamp = Date.now().toString(36).slice(-4).toUpperCase();
  const hash = error.message
    .split("")
    .reduce((a, c) => ((a << 5) - a + c.charCodeAt(0)) | 0, 0);
  const hashStr = Math.abs(hash).toString(36).slice(0, 4).toUpperCase();
  return `SF-${hashStr}-${timestamp}`;
}

class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  ErrorBoundaryState
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null, errorCode: "" };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return {
      hasError: true,
      error,
      errorCode: generateErrorCode(error),
    };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // Log full details for remote debugging
    const errorDetails = {
      code: this.state.errorCode,
      message: error.message,
      name: error.name,
      stack: error.stack,
      component: info.componentStack,
      url: window.location.href,
      userAgent: navigator.userAgent,
      timestamp: new Date().toISOString(),
    };
    console.error("[ERROR_BOUNDARY] Full error details:", JSON.stringify(errorDetails, null, 2));
    console.error("[ERROR_BOUNDARY] Raw error:", error);
  }

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = "/";
  };

  handleCopyCode = () => {
    navigator.clipboard.writeText(
      `Error Code: ${this.state.errorCode}\nMessage: ${this.state.error?.message}\nTime: ${new Date().toISOString()}`
    );
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center p-6 font-['Outfit',sans-serif]">
          <div className="max-w-md w-full text-center space-y-6">
            <div className="w-16 h-16 mx-auto rounded-2xl border-2 border-amber-500/50 flex items-center justify-center"
              style={{ boxShadow: "0 0 30px rgba(240,180,41,0.2)" }}>
              <Shield className="w-8 h-8 text-amber-500" />
            </div>

            <div>
              <h1 className="text-xl font-bold text-white mb-2">Something went wrong</h1>
              <p className="text-sm text-gray-400">
                An unexpected error occurred. Please share the error code below so we can fix it.
              </p>
            </div>

            <div className="bg-white/5 border border-amber-500/30 rounded-xl p-4 space-y-2">
              <p className="text-xs text-gray-500 uppercase tracking-wider">Error Code</p>
              <p className="text-2xl font-mono font-bold text-amber-400 tracking-widest">
                {this.state.errorCode}
              </p>
              <p className="text-xs text-red-400 mt-2 break-all font-mono">
                {this.state.error?.message}
              </p>
              <p className="text-[10px] text-gray-600 mt-1 break-all font-mono max-h-24 overflow-auto">
                {this.state.error?.stack?.split("\n").slice(0, 5).join("\n")}
              </p>
            </div>

            <div className="flex flex-col gap-3">
              <button
                onClick={this.handleCopyCode}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-amber-500/10 border border-amber-500/30 text-amber-400 text-sm font-medium hover:bg-amber-500/20 transition-colors"
              >
                <Copy className="w-4 h-4" /> Copy Error Details
              </button>
              <button
                onClick={this.handleReload}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white text-sm font-medium hover:bg-white/10 transition-colors"
              >
                <RotateCcw className="w-4 h-4" /> Reload Page
              </button>
              <button
                onClick={this.handleGoHome}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-gray-400 text-sm hover:text-white transition-colors"
              >
                <ArrowLeft className="w-4 h-4" /> Back to Website
              </button>
            </div>

            <p className="text-xs text-gray-600">
              Send the error code to support for assistance
            </p>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
